import axios from 'axios';
import { API_BASE, VAPID_PUBLIC_KEY } from '../config';

const HARDCODED_VAPID_KEY = VAPID_PUBLIC_KEY;

const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

const arrayBufferToBase64Url = (buffer) => {
    if (!buffer) return '';
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    const base64 = window.btoa(binary);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

let cachedVapidKey = null;

const getVapidPublicKey = async () => {
    if (cachedVapidKey) return cachedVapidKey;
    try {
        const { data } = await axios.get(`${API_BASE}/push/vapid-public-key`);
        if (data?.publicKey) {
            cachedVapidKey = data.publicKey;
            console.log('[push] fetched VAPID public key from server');
            return cachedVapidKey;
        }
    } catch (error) {
        console.warn('[push] could not fetch VAPID key from server, falling back to config:', error.message || error);
    }
    return HARDCODED_VAPID_KEY;
};

export const isPushSupported = () => {
    return 'serviceWorker' in navigator && 'PushManager' in window;
};

export const getPushPermission = () => {
    return Notification.permission;
};

export const requestPushPermission = async () => {
    if (!isPushSupported()) return 'unsupported';
    if (Notification.permission === 'denied') return 'denied';
    return Notification.requestPermission();
};

export const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) return null;
    try {
        const registration = await navigator.serviceWorker.register('/service-worker.js');
        console.log('[push] service worker registration state:', {
            scope: registration.scope,
            installing: !!registration.installing,
            waiting: !!registration.waiting,
            active: !!registration.active,
        });
        return registration;
    } catch (error) {
        console.error('Service worker registration failed:', error);
        return null;
    }
};

export const subscribeToPush = async () => {
    if (!isPushSupported()) {
        console.warn('[push] not supported');
        return { success: false, error: 'Push not supported' };
    }

    try {
        console.log('[push] requesting permission...');
        const permission = await requestPushPermission();
        console.log('[push] permission:', permission);
        if (permission !== 'granted') {
            return { success: false, error: `Permission ${permission}` };
        }

        console.log('[push] registering service worker...');
        const registration = await registerServiceWorker();
        if (!registration) {
            return { success: false, error: 'Service worker not registered' };
        }
        console.log('[push] service worker registered', registration.scope);

        const publicVapidKey = await getVapidPublicKey();
        if (!publicVapidKey) {
            console.warn('[push] VAPID public key missing');
            return { success: false, error: 'VAPID public key not configured' };
        }
        console.log('[push] subscribing with VAPID key...');

        const existingSubscription = await registration.pushManager.getSubscription();
        if (existingSubscription) {
            console.log('[push] unsubscribing existing subscription to force a fresh one');
            try {
                await existingSubscription.unsubscribe();
            } catch (e) {
                console.warn('[push] unsubscribe failed:', e);
            }
        }

        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicVapidKey),
        });
        console.log('[push] subscription created', subscription);

        await sendSubscriptionToServer(subscription);
        console.log('[push] subscription saved on server');
        return { success: true, subscription };
    } catch (error) {
        console.error('Push subscription failed:', error);
        return { success: false, error: error.message || 'Push subscription failed' };
    }
};

export const unsubscribeFromPush = async () => {
    if (!isPushSupported()) return { success: false, error: 'Push not supported' };
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
            await subscription.unsubscribe();
            await axios.post(
                `${API_BASE}/push/unsubscribe`,
                { endpoint: subscription.endpoint },
                { withCredentials: true }
            );
        }
        return { success: true };
    } catch (error) {
        console.error('Push unsubscribe failed:', error);
        return { success: false, error: error.message || 'Push unsubscribe failed' };
    }
};

const sendSubscriptionToServer = async (subscription) => {
    await axios.post(
        `${API_BASE}/push/subscribe`,
        {
            subscription,
            userAgent: navigator.userAgent,
        },
        { withCredentials: true }
    );
};

export const listenForPushSubscriptionChange = () => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGE' && event.data?.subscription) {
            console.log('[push] received updated subscription from service worker');
            sendSubscriptionToServer(event.data.subscription).catch((err) => {
                console.error('[push] failed to send updated subscription:', err);
            });
        }
    });
};

export const showLocalTestNotification = async () => {
    try {
        const registration = await navigator.serviceWorker.ready;
        await registration.showNotification('LocalPharma test', {
            body: 'If you see this, browser notifications work.',
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag: 'test',
        });
        console.log('[push] local test notification shown');
        return { success: true };
    } catch (error) {
        console.error('[push] local test notification failed:', error);
        return { success: false, error: error.message };
    }
};

if (typeof window !== 'undefined') {
    window.showLocalTestNotification = showLocalTestNotification;
}
